import { useRef, useState } from "react";
import { BoundaryRow } from "../../models/BoundaryRow";
import { ExtractedTable } from "../../models/ExtractedTable";
import { Matcher } from "../../models/matchers/Matcher";
import Page, { PageSpans } from "../../models/Page";
import "../../styles/tableEditing/tableClassification.css";
import { isBodyRow } from "../../types/RowType";
import { OnOffToggle } from "../buttons/OnOffToggle";
import { VisibilityToggle } from "../buttons/VisibilityToggle";
import { Header } from "../Header";
import { HighlightedSpanRows } from "../SpansHighlightable";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTTableViewOcr } from "../tooltips/ConfigurationTooltips";
import {
  TTTableClassificationRulesLabel,
  TTTableHardStopRule,
  TTTableSoftStopRule,
  TTTableStartRule,
  TTTableStartRuleReusable,
} from "../tooltips/TTTables";
import { TableBoundaryEditor } from "./TableBoundaryEditor";
import { TableOffset } from "./TableOffset";
export { TableClassification, Props as TableClassificationProps };

type Props = {
  /**
   * Default BCC Matcher Threshold to set Fuzzy thresholds
   */
  defaultMatcherThreshold: number;
  /** Table Model Reusability */
  generateReuse: boolean;
  /** Table Allow Empty */
  allowEmpty: boolean;
  /** Current offset boolean */
  handleOffset: boolean;
  /** Current offset Start */
  startOffset: number;
  /** Current offset End */
  startOffsetEnd: number;
  /** Table Start Pattern */
  startPattern: Matcher;
  /** Table Soft Stop Pattern */
  softStopPattern: Matcher;
  /** Table Hard Stop Pattern */
  hardStopPattern: Matcher;
  /** AcceleratorDoc Uuid for Table Offset guessing */
  acceleratorDocUuid: string;
  /** Extracted Table Information. Drives Table Offset guessing. */
  extractedTable?: ExtractedTable | undefined;
  /** Spans Promise */
  getPageSpansById: (pageId: string) => Promise<PageSpans>;
  getAllSpans: () => Promise<PageSpans[]>;
  /** List of Pages on the AcceleratorDoc */
  pages: Page[];
  /** Render table offset highlight */
  updateOffset: (
    handleOffset: boolean,
    startOffset: number,
    startOffsetEnd: number,
  ) => void;
  updatePattern: (matcher: Matcher, rule: "start" | "soft" | "hard") => void;
  updateGenerateReuse: (generateReuse: boolean) => void;
  updateAllowEmpty: (allowEmpty: boolean) => void;
};

/**
 * Show configuration options for Table Start, Soft Stop, Hard Stop, and Table Offset controls.
 * Providing an empty AcceleratorDocUUID and undefined ExtractedTableData removes AcceleratorDoc configuration
 * components.
 * @param props
 * @returns
 */
const TableClassification = (props: Props) => {
  const [viewOcr, setShowOcr] = useState(false);
  const pageIds = useRef(
    props.pages.map((x) => x.externalPageIdentifier),
  ).current;

  return (
    // TODO: change class name
    <div className={"tableClassification"}>
      <div>
        <AdvancedTooltip
          tooltipText={TTTableClassificationRulesLabel}
          possiblePlacements={["top-start"]}
        >
          {(tProps) => <Header {...tProps}>Start and Stop Rules</Header>}
        </AdvancedTooltip>

        <div className={"fluffer"} />

        <TableOffset
          update={props.updateOffset}
          acceleratorDocUuid={props.acceleratorDocUuid}
          startPattern={props.startPattern}
          handleOffset={props.handleOffset}
          startOffset={props.startOffset}
          startOffsetEnd={props.startOffsetEnd}
          pages={props.pages}
          getPageSpansById={props.getPageSpansById}
          extractedData={props.extractedTable}
        />
        <AdvancedTooltip overflow tooltipText={TTTableViewOcr}>
          {(tProps) => (
            <VisibilityToggle
              tProps={tProps}
              className={"padded"}
              active={viewOcr}
              onClick={() => setShowOcr(!viewOcr)}
            >
              View OCR
            </VisibilityToggle>
          )}
        </AdvancedTooltip>
      </div>
      {/* object to display start and soft/hard stop pattern */}
      <TableBoundaryEditor
        key={"START" + props.startPattern.getMatcherId()}
        defaultMatcherThreshold={props.defaultMatcherThreshold}
        label={"Start"}
        labelTooltip={TTTableStartRule}
        className={"start"}
        getPageSpansById={props.getPageSpansById}
        pageIds={pageIds}
        matcher={props.startPattern}
        update={(matcher: Matcher) => {
          props.updatePattern(matcher, "start");
        }}
        button={
          <>
            <AdvancedTooltip tooltipText={TTTableStartRuleReusable}>
              {(tProps) => (
                <OnOffToggle
                  tProps={tProps}
                  active={props.generateReuse}
                  onClick={() =>
                    props.updateGenerateReuse(!props.generateReuse)
                  }
                >
                  Reusable
                </OnOffToggle>
              )}
            </AdvancedTooltip>
            <OnOffToggle
              active={props.allowEmpty}
              onClick={() => props.updateAllowEmpty(!props.allowEmpty)}
            >
              Allow Empty
            </OnOffToggle>
          </>
        }
      />
      <TableBoundaryEditor
        key={"SOFT" + props.softStopPattern.getMatcherId()}
        defaultMatcherThreshold={props.defaultMatcherThreshold}
        label={"Soft Stop"}
        labelTooltip={TTTableSoftStopRule}
        className={"soft"}
        pageIds={pageIds}
        getPageSpansById={props.getPageSpansById}
        matcher={props.softStopPattern}
        update={(matcher) => {
          props.updatePattern(matcher, "soft");
        }}
      />
      <TableBoundaryEditor
        key={"HARD" + props.hardStopPattern.getMatcherId()}
        defaultMatcherThreshold={props.defaultMatcherThreshold}
        label={"Hard Stop"}
        labelTooltip={TTTableHardStopRule}
        className={"hard"}
        pageIds={pageIds}
        getPageSpansById={props.getPageSpansById}
        matcher={props.hardStopPattern}
        update={(matcher) => {
          props.updatePattern(matcher, "hard");
        }}
      />
      {viewOcr && props.extractedTable && (
        <HighlightedSpanRows
          key={props.extractedTable?.tableModelId}
          getPageSpansById={props.getPageSpansById}
          pageIds={props.pages.map((x) => x.externalPageIdentifier)}
          highlightedRows={
            props.extractedTable?.rows
              .filter((r) => !isBodyRow(r))
              .map((x) => ({
                pageId: x.pageId ?? "",
                rowNumber: x.pageRowNumber ?? "",
                colorIndex: BoundaryRow.getHighlightColorIndexForRowType(
                  x.rowType,
                ),
              })) ?? null
          }
        />
      )}
    </div>
  );
};
